import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, ExternalLinkIcon } from 'lucide-react';
import { Seo } from '../components/Seo';
import { SectionHeading } from '../components/SectionHeading';
import { TestimonialCarousel } from '../components/TestimonialCarousel';
import { GalleryGrid } from '../components/GalleryGrid';
import { ContactPanel } from '../components/ContactPanel';
import { CtaBand } from '../components/CtaBand';
import { Hero } from '../components/home/Hero';
import { WhyChoose } from '../components/home/WhyChoose';
import { ProcessSteps } from '../components/home/ProcessSteps';
import { useLanguage } from '../contexts/LanguageContext';
import { business } from '../data/business';
import { copy } from '../data/copy';
import { featuredServices } from '../data/services';
import { testimonials } from '../data/testimonials';
export function Home() {
  const {
    t,
    lang
  } = useLanguage();
  const localBusinessSchema = {
    '@context': 'https://schema.org',
    '@type': 'AutoRepair',
    name: business.name,
    image: business.bannerUrl,
    logo: business.logoUrl,
    telephone: '+1-410-942-5091',
    email: business.email,
    inLanguage: ['en', 'es'],
    address: {
      '@type': 'PostalAddress',
      streetAddress: '910 Pulaski Hwy',
      addressLocality: 'Havre De Grace',
      addressRegion: 'MD',
      postalCode: '21078',
      addressCountry: 'US'
    },
    areaServed: 'Harford County, Havre de Grace, Aberdeen, Perryville MD',
    knowsLanguage: ['en', 'es'],
    openingHoursSpecification: [{
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
      opens: '08:00',
      closes: '17:00'
    }],
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: business.rating,
      reviewCount: business.reviewCount
    },
    review: testimonials.map(testimonial => ({
      '@type': 'Review',
      author: {
        '@type': 'Person',
        name: testimonial.name
      },
      reviewRating: {
        '@type': 'Rating',
        ratingValue: testimonial.rating,
        bestRating: 5
      },
      reviewBody: testimonial.quote.en
    }))
  };
  return <>
      <Seo title={t(copy.seo.home.title)} description={t(copy.seo.home.description)} schema={localBusinessSchema} data-id="element-397" data-mp="1fl48f1l28q3k" />

      <Hero data-id="element-398" data-mp="23c096o7huvmy" />
      <WhyChoose data-id="element-399" data-mp="23dbamhdgvraw" />

      <section className="bg-navy-900 py-16 sm:py-20" data-id="element-400" data-mp="28abgosabgu20">
        <div className="mx-auto max-w-7xl px-6" data-id="element-401" data-mp="289s1vkfyqls7">
          <div className="flex flex-wrap items-end justify-between gap-6" data-id="element-402" data-mp="1tlor7xqz6lx2">
            <SectionHeading tone="dark" title={t(copy.servicesHome.title)} description={t(copy.servicesHome.description)} data-id="element-403" data-mp="1tjkqtpsac51j" />
            <Link to="/services" className="inline-flex items-center gap-2 text-sm font-bold text-gold-300 hover:text-gold-200" data-id="element-404" data-mp="2m8rokmjx0qlq">
              {t(copy.cta.viewAllServices)}
              <ArrowRightIcon width={16} height={16} aria-hidden="true" data-id="element-405" data-mp="fjpnfr98dn3a" />
            </Link>
          </div>
          <div className="mt-12 grid gap-x-8 gap-y-10 sm:grid-cols-2 lg:grid-cols-3" data-id="element-406" data-mp="akwk2rnnrcgu">
            {featuredServices.map(service => {
            const Icon = service.icon;
            return <article key={service.slug} className="group flex h-full flex-col border-t border-navy-700 pt-6" data-id="element-407" data-mp="kewp6tixwhpk">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-md bg-navy-800 text-gold-400" data-id="element-408" data-mp="3nuu1p94kq1r2">
                    <Icon width={22} height={22} aria-hidden="true" data-id="element-409" data-mp="5l9xvspuqlvw" />
                  </span>
                  <h3 className="mt-5 font-display text-xl font-bold text-white" data-id="element-410" data-mp="qnsq7ad5pk9">
                    {t(service.name)}
                  </h3>
                  <p className="mt-2 leading-relaxed text-navy-300" data-id="element-411" data-mp="1c4hwppvwp6jm">
                    {t(service.short)}
                  </p>
                  {service.price && <p className="mt-4 font-display text-sm font-bold text-gold-300" data-id="element-412" data-mp="2cczskgr1jx9j">
                      {t(service.price)}
                    </p>}
                  <Link to={`/services#${service.slug}`} className="mt-auto inline-flex items-center gap-1.5 pt-5 text-sm font-bold text-gold-300 transition-colors duration-150 ease-smooth hover:text-gold-200" data-id="element-413" data-mp="2harix6c1fbbm">
                    {t(copy.cta.learnMore)}
                    <ArrowRightIcon width={16} height={16} aria-hidden="true" className="transition-transform duration-200 ease-smooth group-hover:translate-x-1" data-id="element-414" data-mp="2v9hf8jr196yx" />
                  </Link>
                </article>;
          })}
          </div>
        </div>
      </section>

      <ProcessSteps data-id="element-415" data-mp="3e08xkat007pg" />

      <section className="bg-white py-16 sm:py-20" data-id="element-416" data-mp="12ezwq4gigoxi">
        <div className="mx-auto max-w-7xl px-6" data-id="element-417" data-mp="12gjho8d43o5m">
          <div className="grid items-center gap-10 lg:grid-cols-[0.85fr_1.15fr] lg:gap-16" data-id="element-418" data-mp="ydmrnwl8sprn">
            <div data-id="element-419" data-mp="tea4faqglgoj">
              <SectionHeading title={t(copy.testimonials.title)} description={t(copy.testimonials.description)} data-id="element-420" data-mp="tds5g6cewocx" />
              <p className="mt-4 flex items-center gap-2 text-sm font-semibold text-navy-800" data-id="element-421" data-mp="ohbc8xqcvjsz">
                {business.rating}★ · {business.reviewCount}{' '}
                {t(copy.footer.googleReviews)}
              </p>
              <a href={business.reviewsUrl} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-navy-900 hover:text-gold-600" data-id="element-422" data-mp="1ccwbd68nq20d">
                {t(copy.cta.readGoogle)}
                <ExternalLinkIcon width={15} height={15} aria-hidden="true" data-id="element-423" data-mp="2030ap1etbflk" />
              </a>
              {lang === 'es' && <p className="mt-4 text-sm text-navy-500" data-id="element-424" data-mp="3ubne0l47mwsv">
                  {t(copy.pages.reviews.translatedNote)}
                </p>}
            </div>
            <TestimonialCarousel data-id="element-425" data-mp="3kl114ynvokdv" />
          </div>
        </div>
      </section>

      <section className="bg-navy-50 py-16 sm:py-20" data-id="element-426" data-mp="3kiydgla773cb">
        <div className="mx-auto max-w-7xl px-6" data-id="element-427" data-mp="3piks8z5ocrn8">
          <div className="flex flex-wrap items-end justify-between gap-6" data-id="element-428" data-mp="3amlasxwodf5f">
            <SectionHeading title={t(copy.galleryHome.title)} description={t(copy.galleryHome.description)} data-id="element-429" data-mp="3aopp89s79wjy" />
            <Link to="/gallery" className="inline-flex items-center gap-2 text-sm font-bold text-navy-900 hover:text-gold-600" data-id="element-430" data-mp="31mosrf4sjejc">
              {t(copy.cta.seeGallery)}
              <ArrowRightIcon width={16} height={16} aria-hidden="true" data-id="element-431" data-mp="zvlhqoqznxy9" />
            </Link>
          </div>
          <div className="mt-10" data-id="element-432" data-mp="14slkapomqikm">
            <GalleryGrid data-id="element-433" data-mp="14rka0nffu03y" />
          </div>
        </div>
      </section>

      <section className="bg-white py-16 sm:py-20" data-id="element-434" data-mp="1eyd71rqupnjr">
        <div className="mx-auto max-w-7xl px-6" data-id="element-435" data-mp="19yqs9dvdjz8u">
          <SectionHeading title={t(copy.contactHome.title)} description={t(copy.contactHome.description)} data-id="element-436" data-mp="1a01v9bgzx2yw" />
          <div className="mt-10" data-id="element-437" data-mp="1iz6vqnc4b6dp">
            <ContactPanel data-id="element-438" data-mp="1iy44geavv1x9" />
          </div>
        </div>
      </section>

      <CtaBand data-id="element-439" data-mp="1xnt94ji6po2a" />
    </>;
}